// TGCLOUD-1669 Phase 1 — collecting the facts the reporting sections hand up.
//
// Five sidebar sections fetch on mount but keep the answer in their own
// useState, where the collapsed row that renders the summary cannot read it.
// They report it through here instead. Nothing new is fetched.
//
// Two things in this hook are load-bearing and both are easy to get wrong:
//
// 1. THE EQUALITY GUARD. A section re-reports on every render. Without a guard
//    each report would set state, which re-renders, which reports again: a
//    loop. A shallow compare is not enough either, because
//    `clinicalNotes.byType` is a nested object that is rebuilt each time, so
//    identity comparison always sees a change. `factsEqual` is order-insensitive
//    and one level deep for that reason.
//
// 2. THE RESET HAPPENS DURING RENDER, NOT IN AN EFFECT. React flushes effects
//    child-first. In an effect, the sections would report their facts for the
//    newly selected person and this hook would then wipe them immediately
//    afterwards — and since nothing would change in the children again, no
//    re-report would follow and every reported section would stay blank for the
//    rest of the session. Resetting during render happens before the children
//    commit, so their reports land on a clean slate. This is React's documented
//    "adjust state when a prop changes" pattern.

import { useCallback, useRef, useState } from 'react';
import { factsEqual } from './sectionSummary';

/**
 * @param {string|number|null|undefined} subjectId
 *   Identity of the record the facts are about — the selected node's pid. When
 *   it changes, everything already reported is about the wrong patient and is
 *   dropped. Showing one patient's record counts against another patient's
 *   name, even briefly, is the worst state this row can be in.
 * @returns {[Object, (key: string, facts: object) => void]}
 */
export default function useSectionFacts(subjectId) {
  const [facts, setFacts] = useState({});

  const report = useCallback((key, next) => {
    setFacts((prev) => {
      const before = prev[key];
      if (before !== undefined && factsEqual(before, next)) return prev;
      return { ...prev, [key]: next };
    });
  }, []);

  const lastSubjectRef = useRef(subjectId);
  if (lastSubjectRef.current !== subjectId) {
    lastSubjectRef.current = subjectId;
    setFacts({});
  }

  return [facts, report];
}
